import { For, Show, createMemo, createSignal } from 'solid-js'

import { createOutsidePointerDown } from './outsidePointerDown'

type MultiSelectOption = {
   id: string
   name: string
}

type MultiSelectProps = {
   id?: string
   options: MultiSelectOption[]
   selectedIds: string[]
   placeholder?: string
   emptyLabel?: string
   removeLabel?: (name: string) => string
   onToggle: (id: string) => void
   /** When set, typed text that matches no option exactly can be added as a new one. */
   onCreate?: (name: string) => void
   createLabel?: (name: string) => string
}

/**
 * Renders a selected option as a removable chip.
 *
 * @param props - The option, its remove label, and the remove handler.
 * @returns The chip element.
 */
const SelectedChip = (props: {
   option: MultiSelectOption
   removeLabel?: string
   onRemove: () => void
}) => (
   <span class="flex min-w-0 items-center gap-1 rounded-full bg-emerald-500/20 py-0.5 pl-2.5 pr-1 text-xs text-emerald-100">
      <span class="min-w-0 truncate">{props.option.name}</span>
      <button
         type="button"
         class="grid h-4 w-4 shrink-0 place-items-center rounded-full text-emerald-200/80 hover:bg-emerald-500/30 hover:text-emerald-50"
         title={props.removeLabel}
         aria-label={props.removeLabel}
         onClick={event => {
            event.stopPropagation()
            props.onRemove()
         }}
      >
         <svg aria-hidden="true" viewBox="0 0 24 24" class="h-3 w-3">
            <path
               d="M6 6l12 12M18 6 6 18"
               fill="none"
               stroke="currentColor"
               stroke-linecap="round"
               stroke-width="2.5"
            />
         </svg>
      </button>
   </span>
)

/**
 * Renders one dropdown row with a checkbox-style indicator.
 *
 * @param props - The option, whether it is selected, and the toggle handler.
 * @returns The row element.
 */
const OptionRow = (props: {
   option: MultiSelectOption
   selected: boolean
   onToggle: () => void
}) => (
   <button
      type="button"
      role="option"
      aria-selected={props.selected}
      class="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-ink-secondary hover:bg-surface-control"
      onClick={props.onToggle}
   >
      <span
         class="grid h-4 w-4 shrink-0 place-items-center rounded border"
         classList={{
            'border-emerald-400 bg-emerald-500 text-ink-inverse': props.selected,
            'border-edge': !props.selected,
         }}
      >
         <Show when={props.selected}>
            <svg aria-hidden="true" viewBox="0 0 24 24" class="h-3 w-3">
               <path
                  d="M20 6 9 17l-5-5"
                  fill="none"
                  stroke="currentColor"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="3"
               />
            </svg>
         </Show>
      </span>
      <span class="min-w-0 truncate">{props.option.name}</span>
   </button>
)

/**
 * Renders the filtered options listbox, led by a "create" row when the typed
 * text can become a new option.
 *
 * @param props - The options to show, the selection, the create row label, and handlers.
 * @returns The listbox element.
 */
const OptionsDropdown = (props: {
   options: MultiSelectOption[]
   selectedIds: string[]
   emptyLabel?: string
   createLabel?: string
   onToggle: (id: string) => void
   onCreate: () => void
}) => (
   <div
      class="absolute inset-x-0 top-full z-10 mt-1 max-h-44 overflow-y-auto rounded-control border border-edge bg-surface-raised shadow-xl"
      role="listbox"
      aria-multiselectable="true"
   >
      <Show when={props.createLabel}>
         {label => (
            <button
               type="button"
               role="option"
               aria-selected={false}
               class="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-emerald-100 hover:bg-surface-control"
               onClick={() => props.onCreate()}
            >
               <span aria-hidden="true" class="grid h-4 w-4 shrink-0 place-items-center">
                  +
               </span>
               <span class="min-w-0 truncate">{label()}</span>
            </button>
         )}
      </Show>
      <Show
         when={props.options.length > 0 || props.createLabel}
         fallback={<p class="px-3 py-2 text-sm text-ink-faint">{props.emptyLabel}</p>}
      >
         <For each={props.options}>
            {option => (
               <OptionRow
                  option={option}
                  selected={props.selectedIds.includes(option.id)}
                  onToggle={() => props.onToggle(option.id)}
               />
            )}
         </For>
      </Show>
   </div>
)

/**
 * Renders a compact searchable multi-select: selected options appear as
 * removable chips inside the control, and typing filters a dropdown of the
 * remaining options.
 *
 * @param props - The multi-select props.
 * @returns The multi-select element.
 */
const MultiSelect = (props: MultiSelectProps) => {
   let containerRef: HTMLDivElement | undefined
   let inputRef: HTMLInputElement | undefined
   const [isOpen, setOpen] = createSignal(false)
   const [query, setQuery] = createSignal('')

   const selectedOptions = createMemo(() =>
      props.options.filter(option => props.selectedIds.includes(option.id)),
   )
   const filteredOptions = createMemo(() => {
      const needle = query().trim().toLowerCase()
      if (!needle) {
         return props.options
      }
      return props.options.filter(option => option.name.toLowerCase().includes(needle))
   })

   /**
    * Returns the typed text when it can be added as a new option (no option
    * already has that name, ignoring case).
    *
    * @returns The text to create, or null.
    */
   const creatableName = () => {
      const name = query().trim()
      if (!props.onCreate || !name) {
         return null
      }
      const key = name.toLowerCase()
      return props.options.some(option => option.name.toLowerCase() === key) ? null : name
   }
   /**
    * Returns the label of the dropdown's "create" row, or undefined when hidden.
    *
    * @returns The label.
    */
   const createRowLabel = () => {
      const name = creatableName()
      return name ? (props.createLabel?.(name) ?? name) : undefined
   }
   /**
    * Adds the typed text as a new option and refocuses the search input.
    */
   const createOption = () => {
      const name = creatableName()
      if (name) {
         props.onCreate?.(name)
         setQuery('')
         inputRef?.focus()
      }
   }
   /**
    * Closes the dropdown and clears the search text.
    */
   const close = () => {
      setOpen(false)
      setQuery('')
   }
   /**
    * Toggles an option and refocuses the search input.
    *
    * @param id - The option id.
    */
   const toggleOption = (id: string) => {
      props.onToggle(id)
      setQuery('')
      inputRef?.focus()
   }
   /**
    * Handles keyboard shortcuts in the search input: Escape closes, Enter
    * creates the typed option (when allowed) or toggles the first match,
    * Backspace on empty text removes the last chip.
    *
    * @param event - The keydown event.
    */
   const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
         close()
         return
      }
      if (event.key === 'Enter') {
         event.preventDefault()
         const first = filteredOptions()[0]
         if (creatableName()) {
            createOption()
         } else if (isOpen() && first) {
            toggleOption(first.id)
         }
         return
      }
      const last = selectedOptions()[selectedOptions().length - 1]
      if (event.key === 'Backspace' && query() === '' && last) {
         props.onToggle(last.id)
      }
   }

   createOutsidePointerDown(() => containerRef, close)

   return (
      <div ref={containerRef} class="relative">
         <div
            class="flex w-full flex-wrap items-center gap-1.5 rounded-control border border-edge bg-surface-control p-2 focus-within:ring-2 focus-within:ring-green-500"
            onClick={() => {
               inputRef?.focus()
               setOpen(true)
            }}
         >
            <For each={selectedOptions()}>
               {option => (
                  <SelectedChip
                     option={option}
                     removeLabel={props.removeLabel?.(option.name)}
                     onRemove={() => props.onToggle(option.id)}
                  />
               )}
            </For>
            <input
               ref={inputRef}
               id={props.id}
               type="text"
               role="combobox"
               aria-expanded={isOpen()}
               aria-autocomplete="list"
               class="min-w-16 flex-1 bg-transparent p-1 text-sm text-ink placeholder:text-ink-faint focus:outline-none"
               placeholder={selectedOptions().length === 0 ? props.placeholder : ''}
               value={query()}
               onInput={event => {
                  setQuery(event.currentTarget.value)
                  setOpen(true)
               }}
               onFocus={() => setOpen(true)}
               onKeyDown={handleKeyDown}
            />
         </div>
         <Show when={isOpen()}>
            <OptionsDropdown
               options={filteredOptions()}
               selectedIds={props.selectedIds}
               emptyLabel={props.emptyLabel}
               createLabel={createRowLabel()}
               onToggle={toggleOption}
               onCreate={createOption}
            />
         </Show>
      </div>
   )
}

export default MultiSelect
